import { useEffect, useState, type ReactNode } from "react";
import {
  ArrowRight,
  Check,
  Copy,
  ExternalLink,
  Sparkles,
  Terminal,
  Plug,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { store } from "@/lib/store";
import { cn } from "@/lib/utils";

// First-run fork for accounts that arrive from the opzero.sh signup flow. A new
// user lands here with zero machines registered, so neither the session list nor
// the machine list has anything to show — the only useful thing this screen can
// do is offer the two ways in:
//
//   1. install CodeZero on a machine and drive Claude Code from this dashboard
//   2. add the OpZero custom connector in Claude.ai and build without installing
//
// Dismissal is sticky per browser (WELCOME_KEY) so the shell only auto-routes
// here once; the "Get started" sidebar entry keeps it reachable afterwards.

const WELCOME_KEY = "welcomeDone";

export function getWelcomeDone(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(WELCOME_KEY) === "true";
}

export function setWelcomeDone(): void {
  if (typeof window !== "undefined") {
    localStorage.setItem(WELCOME_KEY, "true");
  }
}

// The one-shot machine linking command (see mcps/code/bin/cli.ts). @opzero/code,
// which ships the `codez` binary, is not on npm yet — publishing is still an
// open migration question — so this card deliberately does NOT advertise a
// self-serve install one-liner it cannot honour: opzero.sh/install currently
// hands out the `opzero` deploy CLI, not `codez`. The caveat below says so out
// loud. Once @opzero/code publishes, drop the caveat and add the install
// command above SETUP_COMMAND.
const SETUP_COMMAND = "codez setup";
const INSTALL_GUIDE_URL = "https://opzero.sh/install";

// Canonical OpZero MCP endpoint, same URL the website's Connected Apps tab
// hands out. Claude.ai discovers auth.opzero.sh from it via RFC 9728.
const CONNECTOR_URL = "https://opzero.sh/mcp";
const CLAUDE_CONNECTORS_URL = "https://claude.ai/settings/connectors";

function CopyField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1400);
    return () => window.clearTimeout(id);
  }, [copied]);

  return (
    <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-background/60 px-3 py-2">
      <code className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">
        {value}
      </code>
      <button
        type="button"
        onClick={() => {
          if (typeof navigator === "undefined" || !navigator.clipboard) return;
          navigator.clipboard.writeText(value).then(
            () => setCopied(true),
            () => setCopied(false),
          );
        }}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border/40 bg-secondary/40 text-muted-foreground transition-colors hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
        aria-label={copied ? "Copied" : `Copy ${value}`}
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      </button>
    </div>
  );
}

function NumberedStep({ num, children }: { num: number; children: ReactNode }) {
  return (
    <li className="flex gap-2.5">
      <span className="mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border/60 bg-secondary/40 font-mono text-[9px] text-muted-foreground">
        {num}
      </span>
      <span className="flex-1 text-[11px] leading-relaxed text-muted-foreground">
        {children}
      </span>
    </li>
  );
}

function OptionCard({
  icon,
  eyebrow,
  title,
  description,
  children,
  footer,
}: {
  icon: ReactNode;
  eyebrow: string;
  title: string;
  description: string;
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <div className="glass glass-border card-glow flex flex-col rounded-xl border p-5">
      <div className="mb-3 flex items-center gap-2">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-primary/40 bg-primary/10 text-primary">
          {icon}
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {eyebrow}
        </span>
      </div>
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        {description}
      </p>
      <div className="mt-4 flex-1 space-y-3">{children}</div>
      <div className="mt-5">{footer}</div>
    </div>
  );
}

function ExternalCta({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-primary/40",
        "bg-primary/10 px-3 py-2 text-xs font-medium text-primary transition-colors hover:bg-primary/20",
      )}
    >
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

export function WelcomeView() {
  function handleDismiss() {
    setWelcomeDone();
    store.navigate({ name: "home" });
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-3xl px-4 py-8">
        <header className="mb-8 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-primary">
            <Sparkles className="h-3 w-3" />
            Welcome
          </span>
          <h1 className="gradient-text mt-4 text-2xl font-semibold">
            Two ways to start building
          </h1>
          <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">
            Your account is ready. Pick how you want to work — run Claude Code on
            your own machine, or build straight from a Claude.ai conversation.
            You can do both, and you can come back to this screen any time.
          </p>
        </header>

        <div className="grid gap-4 md:grid-cols-2">
          <OptionCard
            icon={<Terminal className="h-3.5 w-3.5" />}
            eyebrow="Option 1"
            title="Install CodeZero for Claude Code"
            description="Link a machine you own to this dashboard, then start, follow, and steer Claude Code sessions on it from any browser or phone."
            footer={<ExternalCta href={INSTALL_GUIDE_URL} label="Open the install guide" />}
          >
            <ol className="space-y-2">
              <NumberedStep num={1}>
                Get CodeZero onto the machine that holds your repos.
              </NumberedStep>
              <NumberedStep num={2}>
                Run the one-shot setup, which signs the machine in and links it
                to the hub:
              </NumberedStep>
            </ol>
            <CopyField value={SETUP_COMMAND} />
            <p className="rounded-lg border border-border/60 bg-secondary/30 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
              Heads up: the CodeZero installer is not published for self-serve
              install yet, so step 1 is not a one-liner today. The install guide
              has the current options.
            </p>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Once it connects, the machine appears under{" "}
              <button
                type="button"
                onClick={() => store.navigate({ name: "machines" })}
                className="text-primary hover:underline"
              >
                Machines
              </button>
              .
            </p>
          </OptionCard>

          <OptionCard
            icon={<Plug className="h-3.5 w-3.5" />}
            eyebrow="Option 2"
            title="Build with OpZero on Claude.ai"
            description="Add OpZero as a custom connector and deploy sites, MCP servers, and assistants from a Claude conversation. Nothing to install."
            footer={
              <ExternalCta
                href={CLAUDE_CONNECTORS_URL}
                label="Open Claude.ai connectors"
              />
            }
          >
            <ol className="space-y-2">
              <NumberedStep num={1}>
                In Claude.ai, open <span className="text-foreground">Settings</span> →{" "}
                <span className="text-foreground">Connectors</span> →{" "}
                <span className="text-foreground">Add custom connector</span>.
              </NumberedStep>
              <NumberedStep num={2}>Paste this MCP server URL:</NumberedStep>
            </ol>
            <CopyField value={CONNECTOR_URL} />
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Claude sends you to OpZero to authorize. Once it is connected, ask
              Claude to ship something and it lands on your account.
            </p>
          </OptionCard>
        </div>

        <div className="mt-8 flex justify-center">
          <Button variant="ghost" size="sm" onClick={handleDismiss}>
            Skip for now
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default WelcomeView;

